import React from "react";
import { 
  Check, X, ShieldCheck, ArrowRight, Search, HelpCircle, Layers
} from "lucide-react";
import { getCompareArticleBySlug, CompareArticle } from "../../utils/compareRegistry";
import { useSeoMetadata } from "../PublicPages";
import { SeoBreadcrumbs } from "./SeoBreadcrumbs";
import { SeoRelatedLinks } from "./SeoRelatedLinks";
import { 
  SeoSearchActionModule, 
  SeoWhyPageExistsSection 
} from "./SeoUniversalPageEngine";

interface ComparePageProps {
  compareSlug: string;
  onNavigate: (path: string) => void;
  onLogin?: (isSignUp?: boolean) => void;
  currentUser?: any;
}

export function ComparePage({ compareSlug, onNavigate, onLogin = () => {}, currentUser }: ComparePageProps) {
  const comp: CompareArticle | null = getCompareArticleBySlug(compareSlug);

  if (!comp) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6 text-center">
        <h1 className="text-xl font-bold text-slate-800 mb-2">Comparison Page Not Found</h1>
        <p className="text-sm text-slate-600 mb-4">The requested comparison guide does not exist.</p>
        <button
          onClick={() => onNavigate("/compare")}
          className="px-4 py-2 rounded-xl bg-indigo-600 text-white font-medium text-xs hover:bg-indigo-700 transition"
        >
          View All Comparisons
        </button>
      </div>
    );
  }

  const currentRoute = `/compare/${comp.slug}`;

  useSeoMetadata({
    title: comp.title,
    description: comp.metaDescription,
    canonicalUrl: `https://getbiznearyou.com${currentRoute}`,
    schemaJson: {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": comp.h1,
      "description": comp.metaDescription,
      "datePublished": "2026-01-15",
      "dateModified": comp.lastUpdatedDate,
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `https://getbiznearyou.com${currentRoute}`
      },
      "publisher": {
        "@type": "Organization",
        "name": "BizNearYou"
      }
    }
  });

  const handleLinkClick = (e: React.MouseEvent, path: string) => {
    e.preventDefault();
    onNavigate(path);
  };

  return (
    <div className="w-full">
      <main className="max-w-4xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <SeoBreadcrumbs
          items={[
            { label: "Compare", path: "/compare" },
            { label: comp.h1, path: currentRoute }
          ]}
          onNavigate={onNavigate}
        />

        {/* Header */}
        <header className="mb-8">
          <span className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-indigo-100 text-indigo-700 inline-block mb-3">
            Search Strategy Comparison
          </span>
          <h1 className="text-2xl sm:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight leading-tight mb-4">
            {comp.h1}
          </h1>
          <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 leading-relaxed bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800">
            {comp.summary}
          </p>
        </header>

        {/* Interactive Search Box */}
        <div className="mb-8">
          <SeoSearchActionModule
            defaultLocation=""
            defaultKeyword=""
            onNavigate={onNavigate}
          />
        </div>

        {/* Comparison Table */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden mb-8 shadow-xs">
          <div className="p-4 bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white font-bold text-sm border-b border-slate-200 dark:border-slate-700">
            Methodology Comparison Matrix
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-700 dark:text-slate-300 font-bold border-b border-slate-200 dark:border-slate-700">
                <tr>
                  <th className="p-3.5">Dimension</th>
                  <th className="p-3.5 bg-indigo-50/50 dark:bg-indigo-950/40 text-indigo-900 dark:text-indigo-200">BizNearYou Approach</th>
                  <th className="p-3.5">Traditional / Alternative Method</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                {comp.comparisonTable.map((row, i) => (
                  <tr key={i} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition">
                    <td className="p-3.5 font-bold text-slate-900 dark:text-white">{row.feature}</td>
                    <td className="p-3.5 bg-indigo-50/30 dark:bg-indigo-950/30 text-indigo-950 dark:text-indigo-200 font-medium">{row.bizNearYouApproach}</td>
                    <td className="p-3.5 text-slate-600 dark:text-slate-300">{row.alternativeApproach}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Content Sections */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-10 mb-8 space-y-8 shadow-sm">
          {comp.contentSections.map((sec, i) => (
            <section key={i} className="space-y-3">
              <h2 className="text-lg font-bold text-slate-900 tracking-tight">{sec.heading}</h2>
              {sec.paragraphs.map((p, pIdx) => (
                <p key={pIdx} className="text-xs sm:text-sm text-slate-700 leading-relaxed">
                  {p}
                </p>
              ))}
            </section>
          ))}

          {/* Verdict Box */}
          <div className="p-5 rounded-2xl bg-indigo-50 dark:bg-indigo-950/60 text-indigo-950 dark:text-white mt-8 shadow-xs border border-indigo-200 dark:border-indigo-900/60">
            <h3 className="text-xs font-bold uppercase tracking-wider text-indigo-700 dark:text-indigo-300 mb-1">Our Balanced Recommendation</h3>
            <p className="text-xs sm:text-sm text-indigo-900 dark:text-slate-100 leading-relaxed font-medium">{comp.verdict}</p>
          </div>
        </div>

        <SeoRelatedLinks currentRoute={currentRoute} onNavigate={onNavigate} />
      </main>
    </div>
  );
}
